import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

//Material-UI Core Components
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import { CircularProgress, useTheme } from "@mui/material";

//Custom Components
import AppPage from "@/ui/AppPage/AppPage";
import { Permissions } from "@/lib/permissions";
import api from "@/lib/api";
import BarChart from "@/ui/Charts/BarChart"
import { helpers } from "@/lib/helpers";
import DateRangeControl from "@/components/Map/Controls/Custom/DateRangeControl"

// Types
import { DCRecord } from "@/@types/lib/dataController";
import { Card, Typography } from "@mui/material";
import ReportsMap from "../ReportsPage/ReportsMap";

const ROUTE_STATS_PATH = "routing/stats"

function RouteStatsPage() {

  const apiInstance = new api()
  const theme = useTheme();
  const { t } = useTranslation();

  const [rawStatsData, setRawStatsData] = useState<DCRecord | undefined>(undefined);

  const [graphData, setGraphData] = useState<GraphDataType | undefined>(undefined);

  type GraphDataType = {
    graph_days: {
      labels: string[]
      series: number[]
    },
    graph_months: {
      labels: string[]
      series: number[]
    }
  }

  type DataPointDay = {
    day: string,
    count: number
  }

  type DataPointMonth = {
    month: string,
    count: number
  }

  useEffect(()=>{
    getStats();
  },[])

  useEffect(()=>{
    if (rawStatsData)
      parseStats();
  },[rawStatsData])

  const getStats = () => {
    apiInstance.Call(ROUTE_STATS_PATH, "get").then((resp) => {
      const data = resp.data ? resp.data as DCRecord : undefined;
      setRawStatsData(data);
    })
  }

  const parseStats = () => {

    let newGraphData: GraphDataType = { graph_days: { labels: [], series: []}, graph_months: { labels: [], series: []}};
       
    if (rawStatsData && rawStatsData.stat_days && Array.isArray(rawStatsData.stat_days) && rawStatsData.stat_months && Array.isArray(rawStatsData.stat_months)){
      for (const record of rawStatsData.stat_days as DataPointDay[]) {
        newGraphData.graph_days.labels.push(helpers.parseDateStringWithTranslation(record.day, i18n.language as "hr" | "en", "full"));
        newGraphData.graph_days.series.push(record.count);
      }

      for (const record of rawStatsData.stat_months as DataPointMonth[]) {
        newGraphData.graph_months.labels.push(helpers.parseDateStringWithTranslation(record.month, i18n.language as "hr" | "en", "monthYear"));
        newGraphData.graph_months.series.push(record.count);
      }

      setGraphData(newGraphData);
  }
}

const dayGraphOptions = {
  plotOptions: {
    bar: {
      horizontal: false
    }
  }
}

const monthGraphOptions =  {
    plotOptions: {
      bar: {
        horizontal: false
      }
    }
}

  i18n.on('languageChanged', parseStats);

  return (
    <AppPage needPermission={[Permissions.Administration]}>
      <Box m={2}>
        <Grid container spacing={2}>
          <Grid container size={{xs: 12, lg: 6}} spacing={2}>
            <Grid size={12}>
              <Card sx={{overflowX: "auto"}} > 
                <Typography p={2} variant="h5">{t("titles.route_stats_days")}</Typography>
                {graphData ?
                  <BarChart
                    height={"295px"}
                    width={"1250px"}
                    series={[{name: t("routing.number_of_routes"), data: graphData.graph_days.series, color: theme.palette.primary.main}]}
                    labels={graphData.graph_days.labels}
                    handleClick={() => {}}
                    options={dayGraphOptions}
                    title=""
                    categories={[]}
                  />
                  : 
                  <Box width={"100%"} display={"flex"} justifyContent={"center"}>
                     <CircularProgress sx={{m: 5}}/>
                  </Box>
                 
                }                
                
              </Card>
            </Grid>
            <Grid size={12}>
              <Card sx={{overflowX: "auto"}} > 
                <Typography p={2} variant="h5">{t("titles.route_stats_months")}</Typography>

                {graphData ?
                  <BarChart
                    height={"295px"}
                    width={"100%"}
                    series={[{name: t("routing.number_of_routes"), data: graphData.graph_months.series, color: theme.palette.primary.main}]}
                    labels={graphData.graph_months.labels}
                    handleClick={() => {}}
                    options={monthGraphOptions}
                    title=""
                    categories={[]}
                  />
                  : 
                  <Box width={"100%"} display={"flex"} justifyContent={"center"}>
                      <CircularProgress sx={{m: 5}}/>
                  </Box>
                }    
              </Card>
            </Grid>
          </Grid>
          <Grid size={{xs: 12, lg: 6}}>
             <Card sx={{ height: "90vh" }}>
              <Typography gutterBottom variant="h5" component="div" textAlign="center" sx={{width: "100%"}}>
                Učestalost korištenja točaka za rutiranje
              </Typography>
              <ReportsMap />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </AppPage>
  );
}


export default RouteStatsPage;
